import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeConfigNotices,
  dismissConfigNotice,
  clearConfigNotices,
  type ConfigNotice,
} from '../lib/configGuard';

/**
 * Visible banner for the rider anti-data-loss guards:
 *  - a refused save because another session wrote first (409 `stale`), and
 *  - a value the server KEPT because this session did not edit it.
 * Rendered once in App.tsx so it covers every config page (and the
 * musicians / crew pages — lot v0.650). UI text comes from i18n
 * (`configGuard.*`, English by default), consistent with the other banners.
 */
export default function ConfigGuardBanner() {
  const { t } = useTranslation();
  const [notices, setNotices] = useState<ConfigNotice[]>([]);

  useEffect(() => subscribeConfigNotices(setNotices), []);

  // PROBLEMS ONLY (user 2026-10-15: « c'est un message pour dire que tout va
  // bien, est-ce bien utile de l'afficher ? »). A `preserved` notice reports a
  // GOOD outcome — the server KEPT a stored value this session had not edited,
  // so nothing of the user's was lost. Showing them turned the banner into
  // permanent noise, and the very same banner carries the 409 `conflict`: the
  // one case where the user's work was NOT saved. Alert fatigue there is a
  // safety problem (a real conflict gets skipped like the noise), so successes
  // go to the console for diagnosis and only problems reach the screen.
  // (Their root cause is gone too: the autosave now ships only the edited units
  // — see src/lib/configTouched.ts.)
  const noticeIds = notices.map((n) => n.id).join(',');
  useEffect(() => {
    for (const n of notices) if (n.kind === 'preserved') console.info(`[bandz] ${n.message}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the ids: the
    // array identity changes on every emit, which would log the same line twice.
  }, [noticeIds]);

  const visible = notices.filter((n) => n.kind !== 'preserved');
  if (visible.length === 0) return null;

  const textFor = (n: ConfigNotice): string => {
    if (n.kind === 'conflict') return t('configGuard.conflict');
    return n.message;
  };

  return (
    <div className="config-guard-banner" role="alert" aria-live="assertive">
      <div className="config-guard-banner-body">
        {visible.map((n) => (
          <div key={n.id} className={`config-guard-notice config-guard-notice-${n.kind}`}>
            <span className="config-guard-notice-icon" aria-hidden="true">
              {n.kind === 'conflict' ? '⚠️' : 'ℹ️'}
            </span>
            <span className="config-guard-notice-text">{textFor(n)}</span>
            <button
              type="button"
              className="config-guard-notice-close"
              onClick={() => dismissConfigNotice(n.id)}
              aria-label={t('configGuard.dismiss')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      {visible.length > 1 && (
        <button type="button" className="config-guard-banner-close" onClick={clearConfigNotices}>
          {t('configGuard.dismissAll')}
        </button>
      )}
    </div>
  );
}